// Read-only recap of the customer's tarjouspyyntö — left column of
// /tarjouspyynnot/:id. Spec IDs resolve via the wizard's static constants
// (quoteConstants — IDs match dbo.betoni* rows). Open-details model (2026-07-18):
// the backend reveals full customer info to every recipient provider on this
// page (isCustomerInfoRevealed is always true; kept for defensive conditionals).
// When request.betoniLaatuSelvitys.specMode === "guided", a collapsible
// "Miten betonilaatu määritettiin (N)" trail renders below the spec chips;
// "manual" shows a one-liner; null (pre-feature requests) renders nothing.

import { useState } from "react";
import { Box, Chip, Collapse, Link, Stack, Typography, useMediaQuery } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FrostedCard from "src/components/quote/FrostedCard";
import SpecRow from "src/components/quote/SpecRow";
import { staticMapUrl } from "src/utils/valut/staticMapUrl";
import { formatSentDate, localizeDecimal } from "src/utils/valut/valutFormatters";
import { useLang, useT } from "src/i18n";
import { floorPlanSummary } from "src/utils/laskuri/planModel";
import {
    kayttokohdeOptions, lujuusluokkaOptions, raekokoOptions,
    notkeusOptions, kayttoIkaOptions, labelFor,
} from "src/utils/quote/quoteConstants";

// NOTE: this file's own Finnish literals are not yet extracted (Task 19 owns
// this file's i18n) — `t` here is threaded through purely so the shared
// KAYTTOKOHDE/LUJUUSLUOKKA/RAEKOKO/NOTKEUS/KAYTTO_IKA option arrays (converted
// to functions of `t` by Task 18, see quoteConstants.js) render translated
// labels instead of forced Finnish. See src/i18n/fi.js's
// wizard.quoteConstants.options comment.
function specChips(r, t) {
    return [
        labelFor(lujuusluokkaOptions(t), r.lujuusId),
        labelFor(raekokoOptions(t), r.raeKokoId),
        labelFor(notkeusOptions(t), r.notkeusId),
        labelFor(kayttoIkaOptions(t), r.kayttoIkaId),
        r.rasitusluokka || "",
    ].filter(Boolean);
}

export default function RequestRecap({ request, requestAttachments = [] }) {
    const r = request;
    const { lang } = useLang();
    const t = useT();
    const [specOpen, setSpecOpen] = useState(false);
    // DESIGN.md: every transition has a reduced-motion alternative. MUI's
    // Collapse animates height and the chevron rotates; both drop to instant.
    const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
    const mapUrl = r.isCustomerInfoRevealed && r.lat != null && r.lng != null
        ? staticMapUrl({ lat: r.lat, lng: r.lng, w: 480, h: 200 })
        : null;

    return (
        <FrostedCard sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t("provider.recap.heading")}</Typography>

            {mapUrl && (
                <Box
                    sx={{
                        width: "100%", height: 160, borderRadius: 1, mb: 1.5,
                        backgroundImage: `url("${mapUrl.replace(/"/g, "%22")}")`,
                        backgroundSize: "cover", backgroundPosition: "center",
                    }}
                    role="img"
                    aria-label={t("provider.recap.mapAlt", { address: r.formattedAddress || r.osoite })}
                />
            )}

            <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", rowGap: 0.5, mb: 1.5 }}>
                {specChips(r, t).map((label) => (
                    <Chip key={label} size="small" label={label} />
                ))}
            </Stack>

            {r.betoniLaatuSelvitys?.specMode === "guided"
                && r.betoniLaatuSelvitys.steps?.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                    <Stack
                        direction="row"
                        onClick={() => setSpecOpen((o) => !o)}
                        role="button"
                        tabIndex={0}
                        aria-expanded={specOpen}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setSpecOpen((o) => !o);
                            }
                        }}
                        sx={{ alignItems: "center", cursor: "pointer", userSelect: "none" }}
                    >
                        <Typography variant="caption" sx={{ color: "text.secondary", flex: 1 }}>
                            {t("provider.recap.guidedQuestionsCount", { count: r.betoniLaatuSelvitys.steps.length })}
                        </Typography>
                        <ExpandMoreIcon
                            fontSize="small"
                            sx={{
                                transform: specOpen ? "rotate(180deg)" : "none",
                                transition: reduceMotion ? "none" : "transform 120ms",
                                color: "text.secondary",
                            }}
                        />
                    </Stack>
                    <Collapse in={specOpen} timeout={reduceMotion ? 0 : "auto"}>
                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                            {r.betoniLaatuSelvitys.steps.map((s) => (
                                <SpecRow key={s.question} label={s.question} value={s.answer} />
                            ))}
                        </Stack>
                    </Collapse>
                </Box>
            )}
            {r.betoniLaatuSelvitys?.specMode === "manual" && (
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>
                    {t("provider.recap.manualSpecNotice")}
                </Typography>
            )}

            <Stack spacing={0.75}>
                <SpecRow label={t("provider.recap.addressLabel")} value={r.formattedAddress || r.osoite} />
                {r.asiakasNimi && (
                    <SpecRow
                        label={t("provider.recap.customerLabel")}
                        value={`${r.asiakasNimi} (${r.asiakasTypeId === 1 ? t("provider.shared.companyType") : t("provider.shared.privateType")})`}
                    />
                )}
                {/* The revealed contact is the whole payoff of bidding; on a phone
                    at a yard it has to be one tap away. Inherit the row's ink: the
                    theme's teal link colour is 3.5:1 on this card. */}
                {r.isCustomerInfoRevealed && r.customerPhone && (
                    <SpecRow
                        label={t("provider.recap.phoneLabel")}
                        value={<Link href={`tel:${r.customerPhone.replace(/\s+/g, "")}`} color="inherit">{r.customerPhone}</Link>}
                    />
                )}
                {r.isCustomerInfoRevealed && r.customerEmail && (
                    <SpecRow
                        label={t("provider.recap.emailLabel")}
                        value={<Link href={`mailto:${r.customerEmail}`} color="inherit">{r.customerEmail}</Link>}
                    />
                )}
                <SpecRow label={t("provider.recap.kayttokohdeLabel")} value={labelFor(kayttokohdeOptions(t), r.kayttokohde, r.kayttokohde)} />
                {/* These four are SQL decimals (totalM3 8,2 · pumppuKesto 5,2 ·
                    requiredPuomi 5,2 · requiredLinja 6,2), so a half-cube job
                    interpolated as "7.5 m³" — a dot decimal in Finnish copy.
                    localizeDecimal swaps the separator without rounding or
                    padding, which is what a value whose precision we do not
                    control needs. */}
                <SpecRow label={t("provider.recap.amountLabel")} value={r.totalM3 ? `${localizeDecimal(r.totalM3, lang)} m³` : ""} />
                {floorPlanSummary(r.betoniLaatuSelvitys?.floorPlan, lang, t("laskuri.floorPlanSummary.recommendedWord")) && (
                    <SpecRow
                        label={t("provider.recap.floorPlanLabel")}
                        value={floorPlanSummary(r.betoniLaatuSelvitys?.floorPlan, lang, t("laskuri.floorPlanSummary.recommendedWord"))}
                    />
                )}
                <SpecRow label={t("provider.recap.durationLabel")} value={r.pumppuKesto ? `${localizeDecimal(r.pumppuKesto, lang)} h` : ""} />
                <SpecRow label={t("provider.recap.boomLabel")} value={r.requiredPuomi ? `${localizeDecimal(r.requiredPuomi, lang)} m` : ""} />
                <SpecRow label={t("provider.recap.lineLabel")} value={r.requiredLinja ? `${localizeDecimal(r.requiredLinja, lang)} m` : ""} />
                <SpecRow label={t("provider.recap.concreteDescLabel")} value={r.betonikuvaus || ""} />
                <SpecRow label={t("provider.recap.driveInstructionsLabel")} value={r.ajoOhjeet || ""} />
                <SpecRow label={t("provider.recap.notesLabel")} value={r.notes || ""} />
            </Stack>

            {requestAttachments.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>{t("provider.recap.customerAttachmentsHeading")}</Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
                        {requestAttachments.map((a) => (
                            a.fileType?.startsWith("image/") && a.url ? (
                                <Link key={a.attachmentId} href={a.url} target="_blank" rel="noopener">
                                    <Box
                                        component="img"
                                        src={a.url}
                                        alt={a.origFileName}
                                        sx={{ width: 88, height: 66, objectFit: "cover", borderRadius: 1 }}
                                    />
                                </Link>
                            ) : (
                                <Link key={a.attachmentId} href={a.url} target="_blank" rel="noopener" variant="body2">
                                    📎 {a.origFileName}
                                </Link>
                            )
                        ))}
                    </Stack>
                </Box>
            )}

            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 2 }}>
                {r.expiresAt ? t("provider.recap.respondByPrefix", { date: formatSentDate(r.expiresAt, lang) }) : ""}
                {t("provider.recap.footerLine", { id: r.pumppuRequestId, date: formatSentDate(r.createdAt, lang) })}
            </Typography>
        </FrostedCard>
    );
}
